import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Newspaper, Radio, Award, TrendingUp, CheckCircle2, ArrowRight, Zap, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PressAndPRPage = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const features = [
    {
      icon: <Newspaper className="w-8 h-8" />,
      title: "Press Release Writing & Distribution",
      description: "Get your story in front of journalists",
      detail: "We craft compelling press releases and distribute them to relevant media outlets, journalists, and industry publications to generate coverage."
    },
    {
      icon: <Megaphone className="w-8 h-8" />,
      title: "Media Outreach & Pitching",
      description: "Build relationships with media contacts",
      detail: "We identify target media outlets, build relationships with journalists, and pitch your stories to secure interviews, features, and mentions."
    },
    {
      icon: <Radio className="w-8 h-8" />,
      title: "Crisis Communication & Reputation Management",
      description: "Protect and manage your brand reputation",
      detail: "We handle crisis communications, respond to negative press, and implement reputation management strategies to safeguard your brand image."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Thought Leadership & Expert Positioning",
      description: "Establish yourself as an industry authority",
      detail: "We position you as an expert through media appearances, guest articles, podcast interviews, and speaking opportunities."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "PR Strategy & Media Planning",
      description: "Data-driven PR campaigns that deliver",
      detail: "We develop comprehensive PR strategies with clear objectives, target media lists, messaging frameworks, and measurement plans."
    }
  ];

  const process = [
    {
      step: "01",
      title: "PR Audit & Goal Setting",
      description: "We analyze your current media presence, identify opportunities, and define clear PR objectives aligned with your business goals."
    },
    {
      step: "02",
      title: "Media List Development",
      description: "We build a targeted list of journalists, publications, podcasts, and media outlets relevant to your industry and audience."
    },
    {
      step: "03",
      title: "Content Creation & Messaging",
      description: "We craft press releases, media kits, pitch angles, and key messages that resonate with journalists and tell your story effectively."
    },
    {
      step: "04",
      title: "Media Outreach & Pitching",
      description: "We pitch your stories to targeted media contacts, follow up persistently, and secure interviews, features, and coverage."
    },
    {
      step: "05",
      title: "Monitoring & Reporting",
      description: "We track media coverage, measure PR impact, and provide detailed reports on reach, sentiment, and ROI."
    }
  ];

  const benefits = [
    "Build brand credibility and trust through earned media",
    "Reach wider audiences through press and media coverage",
    "Establish thought leadership and industry authority",
    "Manage your reputation and handle crisis communications",
    "Generate high-value PR that drives business growth"
  ];

  const idealFor = [
    "Businesses launching new products or services",
    "Founders wanting to build personal brand and thought leadership",
    "Brands needing reputation management or crisis communication",
    "Companies seeking media coverage and press opportunities",
    "Businesses wanting to establish industry authority"
  ];

  return (
    <div className="min-h-screen bg-black">
      <Header onContactClick={scrollToContact} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-brand-brown/20 to-black"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-brand-maroon/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-brand-olive/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-brand-maroon/20 rounded-full border border-brand-maroon/40 mb-6">
              <span className="text-brand-maroon font-bold">SERVICES</span>
            </div>
            <h1 className="text-6xl lg:text-7xl font-bold text-white mb-6 drop-shadow-[0_0_30px_rgba(255,255,255,0.5)]">
              Press &{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon via-brand-olive to-brand-maroon drop-shadow-[0_0_20px_rgba(139,35,50,0.8)]">
                PR
              </span>
            </h1>
            <p className="text-2xl text-white leading-relaxed max-w-3xl mx-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
              Earn media coverage, build credibility, and establish thought leadership. Strategic PR that gets you noticed by the right people.
            </p>
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient1)" />
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(139, 35, 50, 0.15)" />
              <stop offset="50%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="100%" stopColor="rgba(139, 35, 50, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* What's Included Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-brown/10 to-black">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              What's{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Included
              </span>
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Comprehensive PR services — from press releases to media relationships.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl p-8 border-2 border-brand-olive/30 hover:border-brand-maroon/50 transition-all duration-300 hover:scale-105 shadow-2xl"
              >
                <div className="w-16 h-16 bg-brand-maroon/20 rounded-xl flex items-center justify-center mb-6 text-brand-maroon">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                  {feature.title}
                </h3>
                <p className="text-brand-olive font-semibold mb-3 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  {feature.description}
                </p>
                <p className="text-white/80 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  {feature.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient2)" />
          <defs>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(139, 35, 50, 0.15)" />
              <stop offset="50%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="100%" stopColor="rgba(139, 35, 50, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Process Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-brand-brown/10">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Process
              </span>
            </h2>
            <p className="text-xl text-white/80">
              From strategy to execution — here's how we secure media coverage for your brand.
            </p>
          </div>

          <div className="space-y-8">
            {process.map((item, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-brand-brown/40 to-black/60 backdrop-blur-xl rounded-2xl p-8 border border-brand-olive/30 hover:border-brand-maroon/50 transition-all duration-300 shadow-lg flex items-start gap-6"
              >
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-brand-maroon to-brand-olive rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  {item.step}
                </div>
                <div className="flex-grow">
                  <h3 className="text-2xl font-bold text-white mb-3 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                    {item.title}
                  </h3>
                  <p className="text-white/80 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits & Ideal For Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-brown/10 to-black">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Benefits */}
            <div className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl p-10 border-2 border-brand-maroon/50 shadow-2xl">
              <div className="flex items-center gap-3 mb-8">
                <Zap className="w-10 h-10 text-brand-maroon" />
                <h3 className="text-3xl font-bold text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]">
                  Why It{' '}
                  <span className="text-brand-maroon">Works</span>
                </h3>
              </div>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-brand-maroon flex-shrink-0 mt-1" />
                    <span className="text-white/90 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ideal For */}
            <div className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl p-10 border-2 border-brand-olive/50 shadow-2xl">
              <div className="flex items-center gap-3 mb-8">
                <Users className="w-10 h-10 text-brand-olive" />
                <h3 className="text-3xl font-bold text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]">
                  Ideal{' '}
                  <span className="text-brand-olive">For</span>
                </h3>
              </div>
              <ul className="space-y-4">
                {idealFor.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ArrowRight className="w-6 h-6 text-brand-olive flex-shrink-0 mt-1" />
                    <span className="text-white/90 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-brand-brown/10">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="bg-gradient-to-br from-brand-maroon/20 to-brand-olive/20 backdrop-blur-xl rounded-3xl p-12 border-2 border-brand-maroon/50 shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              Ready to Earn{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Media Coverage?
              </span>
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Let's secure press coverage, build your reputation, and establish you as an industry authority.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={scrollToContact}
                className="bg-brand-maroon hover:bg-brand-maroon/90 text-white font-bold text-lg px-12 py-6 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg shadow-brand-maroon/50"
              >
                Book Free PR Consultation
              </Button>
              <Button
                onClick={() => navigate('/packages/basic')}
                variant="outline"
                className="border-2 border-brand-olive text-brand-olive hover:bg-brand-olive hover:text-black font-bold text-lg px-12 py-6 rounded-xl transition-all duration-300"
              >
                View Pricing Packages
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PressAndPRPage;